// Which defeat copy to show, by language.
//
// The same shape as `content/rules`, deliberately: one entry per locale, each
// a *dynamic* import, so adding a language is a file and a line here and costs
// a download for the people who read it rather than bundle weight for
// everybody who does not.
//
// It shares the rules' locale picker rather than growing a second one — a
// player reading the rules in English and being told they lost in German would
// be one preference answered twice.

import type { ComponentType } from "react";
import type { MDXComponents } from "mdx/types";
import type { DefeatReason } from "src/client/ai/ui/defeat";

/**
 * What the document is handed. Mirrored in `en.mdx`'s header comment.
 *
 * A `type` rather than an `interface`, and that is not a style choice: a
 * compiled `.mdx` module takes `MDXProps`, which is an index signature, and
 * only an object *type* gets the implicit index signature that makes it
 * assignable to one. As an interface this fails to compile with a message
 * about `string` that says nothing about MDX.
 */
export type DefeatProps = {
  /** Which ending it was. The document keys its three lines off this. */
  reason: DefeatReason;
  /** How far the run got. */
  stage: number;
};

export type DefeatDoc = ComponentType<DefeatProps & { components?: MDXComponents }>;

export const DEFEAT_LOCALES: Record<string, () => Promise<{ default: DefeatDoc }>> = {
  en: () => import("./en.mdx")
};

/** The document for a locale code, falling back to English. */
export const defeatDoc = (code: string): (() => Promise<{ default: DefeatDoc }>) =>
  DEFEAT_LOCALES[code] ?? DEFEAT_LOCALES.en;
